import React, { useState, useEffect, useRef } from 'react';
import { BiExport, BiMessageDetail, BiHash, BiGroup, BiFilterAlt, BiErrorCircle, BiSortAlt2, BiDownArrowAlt, BiUpArrowAlt, BiTimeFive } from 'react-icons/bi';
import { BsThreeDots, BsDownload } from 'react-icons/bs';

import { CSVLink } from 'react-csv';
import { orderBy } from 'lodash';
import { Popover, PopoverBody } from 'reactstrap';

import axios from '../../../axios';
import * as url from '../../../utils/url';
import { formatTimeAgo } from '../../../utils/stringUtils';
import { toastContainer } from '../../../utils/toast';
import BottomScrollListener from 'react-bottom-scroll-listener';
import OoPopover from '../../../components/UI/Popover/OoPopover';

const HashtagOverview = ({ history, match }) => {
    const containerRef = useRef(null);
    const [hashtags, setHashtags] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [nextSkip, setNextSkip] = useState(0);
    const [hasMore, setHasMore] = useState(false);
    const [filter, setFilter] = useState('All');
    const [metricStats, setMetricStats] = useState({ activeGroups: 0, uniqueParticipants: 0 });
    const [sortConfig, setSortConfig] = useState({ key: 'activity', direction: 'desc' });
    const [optionsPopoverOpen, setOptionsPopoverOpen] = useState(null);

    const stateRef = useRef(null);

    // Keep stateRef up to date with the latest state for unmounting
    useEffect(() => {
        stateRef.current = { hashtags, nextSkip, hasMore, metricStats };
    }, [hashtags, nextSkip, hasMore, metricStats]);

    useEffect(() => {
        const savedState = sessionStorage.getItem('uc_hashtag_overview_state');
        const isFromDrilldown = sessionStorage.getItem('uc_from_drilldown') === 'true';

        // Clear flag immediately so fresh navigations (like clicking the sidebar tab) start clean
        sessionStorage.removeItem('uc_from_drilldown');

        // Only restore if we are specifically navigating back from drilldown AND there is data
        if (savedState && isFromDrilldown) {
            const parsed = JSON.parse(savedState);
            if (parsed.hashtags && parsed.hashtags.length > 0) {
                setHashtags(parsed.hashtags);
                setNextSkip(parsed.nextSkip);
                setHasMore(parsed.hasMore);
                setMetricStats(parsed.metricStats);
                setLoading(false);

                // Restore scroll after a delay to allow the table to render with data
                setTimeout(() => {
                    if (containerRef.current) {
                        containerRef.current.scrollTop = parsed.scrollTopValue || 0;
                    }
                }, 100);
            } else {
                fetchHashtags(0);
            }
        } else {
            // Start completely fresh
            sessionStorage.removeItem('uc_hashtag_overview_state');
            fetchHashtags(0);
        }

        // Save current state AND scroll position exactly once when unmounting/navigating away
        return () => {
            if (stateRef.current && stateRef.current.hashtags.length > 0) {
                const stateToSave = {
                    ...stateRef.current,
                    scrollTopValue: containerRef.current ? containerRef.current.scrollTop : 0
                };
                sessionStorage.setItem('uc_hashtag_overview_state', JSON.stringify(stateToSave));
            }
        };
    }, []);

    const fetchHashtags = async (skipValue = 0) => {
        try {
            if (skipValue === 0) setLoading(true);
            else setLoadingMore(true);

            const res = await axios.get(`${url.textifyListGroups}?skip=${skipValue}`);
            const data = (res.data && res.data.items) || [];

            const total = (res.data && res.data.total) || data.length;
            const hasMore = (res.data && res.data.hasMore !== undefined) ? res.data.hasMore : (data.length === 25);
            const nextSkip = (res.data && res.data.nextSkip !== undefined) ? res.data.nextSkip : (skipValue + data.length);

            const formattedData = data.map(item => ({
                id: item._id,
                hashtag: item.groupName.startsWith('#') ? item.groupName.slice(1) : item.groupName,
                users: item.uniqueParticipantCount || 0,
                activity: item.messageCount || 0,
                lastActivity: item.lastActivityAt ? formatTimeAgo(item.lastActivityAt) : '—',
                lastActivityTs: item.lastActivityAt ? new Date(item.lastActivityAt).getTime() : 0,
                sentiment: (item.averageSentimentsScore && item.averageSentimentsScore.sentiment) || null,
                needsAttention: item.needsAttentionCount || 0,
                newActivity: false,
            }));

            if (skipValue === 0) {
                setHashtags(formattedData);
                // The API gives `total` for active groups and optionally `uniqueParticipants`
                const participantsSum = (res.data && res.data.uniqueParticipants) ||
                    data.reduce(function(sum, item) { return sum + (item.uniqueParticipantCount || 0); }, 0);
                setMetricStats({
                    activeGroups: total,
                    uniqueParticipants: participantsSum
                });
            } else {
                setHashtags(prev => [...prev, ...formattedData]);
            }

            setNextSkip(nextSkip);
            setHasMore(hasMore);

        } catch (error) {
            console.error('[Hashtag Overview] Error fetching groups:', error);
        } finally {
            if (skipValue === 0) setLoading(false);
            else setLoadingMore(false);
        }
    };

    const handleBottomScroll = () => {
        if (hasMore && !loading && !loadingMore) {
            fetchHashtags(nextSkip);
        }
    };

    const handleRowClick = (hashtag, groupId) => {
        // Drop a breadcrumb that we are leaving to go to drilldown, so we know to restore scroll on return
        sessionStorage.setItem('uc_from_drilldown', 'true');
        history.push(`${match.url}/${hashtag.toLowerCase()}/${groupId}`);
    };

    const handleSort = (key) => {
        let direction = 'desc';
        if (sortConfig.key === key && sortConfig.direction === 'desc') {
            direction = 'asc';
        }
        setSortConfig({ key, direction });
    };

    const handleExportThread = async (item) => {
        try {
            toastContainer('Preparing export...', 'info');
            var allMessages = [];
            var skip = 0;
            var pageSize = 100;
            var moreToFetch = true;

            while (moreToFetch) {
                var res = await axios.get(url.textifyGetGroupMessages(item.id) + '?limit=' + pageSize + '&skip=' + skip);
                var messages = (res.data && res.data.messages) || [];
                allMessages = allMessages.concat(messages);
                moreToFetch = !!(res.data && res.data.hasMore) && messages.length === pageSize;
                skip = skip + messages.length;
                if (skip >= 1000) break; // safety cap
            }

            if (allMessages.length === 0) {
                toastContainer('No messages to export.', 'info');
                return;
            }

            var rows = allMessages.map(function(m) {
                var senderName = (m.userId && typeof m.userId === 'object')
                    ? (m.userId.firstName + ' ' + m.userId.lastName).trim()
                    : (m.isRecipient ? 'Recipient' : 'Participant');
                var phone = (!m.isRecipient && m.userId && m.userId.mobileNumber) || '';
                var date = m.createdAt ? new Date(m.createdAt).toLocaleString() : '';
                var body = (m.body || '').replace(/"/g, '""');
                return '"' + date + '","' + senderName + '","' + phone + '","' + body + '"';
            });

            var csvContent = '"Date","Sender","Phone","Message"\n' + rows.join('\n');
            var blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            var downloadUrl = URL.createObjectURL(blob);
            var link = document.createElement('a');
            link.href = downloadUrl;
            link.download = '#' + item.hashtag + '-thread-export.csv';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(downloadUrl);
            toastContainer('Export downloaded successfully', 'success');
        } catch (err) {
            console.error('[HashtagOverview] Export thread error:', err);
            toastContainer('Failed to export thread.', 'error');
        }
    };

    const handleDropdownAction = (e, actionType, item) => {
        e.stopPropagation();
        setOptionsPopoverOpen(null);
        if (actionType === 'OPEN') {
            handleRowClick(item.hashtag, item.id);
        }
        if (actionType === 'EXPORT') {
            handleExportThread(item);
        }
    };

    const renderSortIcon = (key) => {
        if (sortConfig.key === key) {
            return sortConfig.direction === 'asc' ? <BiUpArrowAlt size={14} className="ms-1" /> : <BiDownArrowAlt size={14} className="ms-1" />;
        }
        return <BiSortAlt2 size={14} className="ms-1 text-muted" style={{ opacity: 0.5 }} />;
    };

    const filteredHashtags = hashtags.filter(h => filter === 'All' || h.sentiment === filter);
    const sortedHashtags = orderBy(filteredHashtags, [sortConfig.key], [sortConfig.direction]);

    const csvHeaders = [
        { label: "Hashtag", key: "hashtag" },
        { label: "Participants", key: "users" },
        { label: "Activity", key: "activity" },
        { label: "Last Activity", key: "lastActivity" },
    ];

    return (
        <div className="uc-overview-page">
            <div className="uc-header-area">
                <h1>User Chats</h1>
                <p>Shared SMS inbox organized by hashtag channel. Visible only to users included as hashtag recipients.</p>
            </div>

            <div className="uc-metrics-cards">
                <div className="uc-metric-card">
                    <div className="icon-wrapper"><BiHash /></div>
                    <div className="uc-metric-data">
                        <strong>{metricStats.activeGroups}</strong>
                        <span>Chat Groups</span>
                    </div>
                </div>
                <div className="uc-metric-card">
                    <div className="icon-wrapper"><BiGroup /></div>
                    <div className="uc-metric-data">
                        <strong>{metricStats.uniqueParticipants.toLocaleString()}</strong>
                        <span>Unique Participants</span>
                    </div>
                </div>
            </div>

            <div className="uc-table-actions">
                <div className="uc-sentiment-filter">
                    {/* Sentiment filtering UI is not included in this phase */}
                </div>
                <CSVLink data={sortedHashtags} headers={csvHeaders} filename={`User-Chats-Export.csv`} style={{ textDecoration: 'none' }}>
                    <button className="uc-export-btn"><BsDownload /> Export CSV</button>
                </CSVLink>
            </div>

            <BottomScrollListener offset={50} onBottom={handleBottomScroll}>
                {(scrollRef) => (
                    <div
                        className="uc-table-container"
                        ref={(node) => {
                            if (typeof scrollRef === 'function') {
                                scrollRef(node);
                            } else if (scrollRef && typeof scrollRef === 'object') {
                                scrollRef.current = node;
                            }
                            containerRef.current = node;
                        }}
                        style={{ maxHeight: '600px', overflowY: 'auto', WebkitOverflowScrolling: 'touch' }}
                    >
                        <table className="uc-table uc-table-cards">
                            <thead>
                                <tr>
                                    <th className="cursor-pointer select-none" onClick={() => handleSort('hashtag')}>
                                        HASHTAG {renderSortIcon('hashtag')}
                                    </th>
                                    <th className="select-none">
                                        <div className="d-flex align-items-center cursor-pointer">
                                            <div className="d-flex align-items-center" onClick={() => handleSort('users')}>
                                                <BiGroup className="me-1" size={14} />PARTICIPANTS {renderSortIcon('users')}
                                            </div>
                                            <div onClick={e => e.stopPropagation()} style={{ marginLeft: '4px' }}>
                                                <OoPopover
                                                    target="participants-tooltip"
                                                    placement="top"
                                                    headerText="Participants"
                                                    bodyText="Number of unique users who have sent at least one chat with that hashtag."
                                                />
                                            </div>
                                        </div>
                                    </th>
                                    <th className="select-none">
                                        <div className="d-flex align-items-center cursor-pointer">
                                            <div className="d-flex align-items-center" onClick={() => handleSort('activity')}>
                                                ACTIVITY {renderSortIcon('activity')}
                                            </div>
                                            <div onClick={e => e.stopPropagation()} style={{ marginLeft: '4px' }}>
                                                <OoPopover
                                                    target="activity-tooltip"
                                                    placement="top"
                                                    headerText="Activity"
                                                    bodyText="Total number of chats by participants and recipients."
                                                />
                                            </div>
                                        </div>
                                    </th>
                                    <th className="select-none">
                                        <div className="d-flex align-items-center cursor-pointer">
                                            <div className="d-flex align-items-center" onClick={() => handleSort('lastActivityTs')}>
                                                <BiTimeFive className="me-1" size={14} />LAST ACTIVITY {renderSortIcon('lastActivityTs')}
                                            </div>
                                            <div onClick={e => e.stopPropagation()} style={{ marginLeft: '4px' }}>
                                                <OoPopover
                                                    target="last-activity-tooltip"
                                                    placement="top"
                                                    headerText="Last Activity"
                                                    bodyText="Last chat sent by a participant or a recipient."
                                                />
                                            </div>
                                        </div>
                                    </th>
                                    {/* <th className="select-none">
                                        <div className="d-flex align-items-center cursor-pointer">
                                            <div className="d-flex align-items-center" onClick={() => handleSort('sentiment')}>
                                                AVG SENTIMENT {renderSortIcon('sentiment')}
                                            </div>
                                            <div onClick={e => e.stopPropagation()} style={{ marginLeft: '4px' }}>
                                                <OoPopover
                                                    target="sentiment-tooltip"
                                                    placement="top"
                                                    headerText="Avg Sentiment"
                                                    bodyText="Average sentiment of chats sent by all participants."
                                                />
                                            </div>
                                        </div>
                                    </th> */}
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr><td colSpan="6" className="text-center py-4">Loading...</td></tr>
                                ) : (
                                    <>
                                        {sortedHashtags.map(item => (
                                            <tr key={item.id} onClick={() => handleRowClick(item.hashtag, item.id)} className="cursor-pointer uc-card-row">
                                                <td>
                                                    <div className="uc-hashtag-col text-bold">
                                                        <span className="hash-icon">#</span> {item.hashtag}
                                                    </div>
                                                </td>
                                                <td className="uc-fw-500">{item.users}</td>
                                                <td className="uc-fw-500">{item.activity}</td>
                                                <td className="uc-fw-400-muted">{item.lastActivity}</td>
                                                {/* <td>
                                                    {item.sentiment ? (
                                                        <span className={`uc-pill sentiment-${item.sentiment.toLowerCase()}`}>
                                                            <span className="dot">●</span> {item.sentiment}
                                                        </span>
                                                    ) : (
                                                        <span className="text-muted">—</span>
                                                    )}
                                                </td> */}
                                                <td onClick={e => e.stopPropagation()}>
                                                    <div className="cursor-pointer" style={{ position: 'relative' }}>
                                                        <span
                                                            id={`hashtagListRow_${item.id}`}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setOptionsPopoverOpen(optionsPopoverOpen === item.id ? null : item.id);
                                                            }}
                                                            className="cursor-pointer">
                                                            <BsThreeDots className="text-muted" size={18} />
                                                        </span>

                                                        <Popover
                                                            isOpen={optionsPopoverOpen === item.id}
                                                            target={`hashtagListRow_${item.id}`}
                                                            toggle={() => setOptionsPopoverOpen(null)}
                                                            placement="bottom"
                                                            hideArrow
                                                            className="surveyListRow_Popover">
                                                            <PopoverBody className="p-0 py-2">
                                                                <div
                                                                    className="dropdown-item py-1 px-3 cursor-pointer"
                                                                    onClick={(e) => handleDropdownAction(e, 'OPEN', item)}>
                                                                    View conversations
                                                                </div>
                                                                <div
                                                                    className="dropdown-item py-1 px-3 cursor-pointer"
                                                                    onClick={(e) => handleDropdownAction(e, 'EXPORT', item)}>
                                                                    Export thread
                                                                </div>
                                                            </PopoverBody>
                                                        </Popover>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                        {loadingMore && (
                                            <tr><td colSpan="6" className="text-center py-3 text-muted">Loading more groups...</td></tr>
                                        )}
                                    </>
                                )}
                            </tbody>
                        </table>
                        {!loading && (
                            <div className="uc-infinite-scroll-indicator d-flex flex-column gap-1">
                                <span className="text-muted">Recipients are notified on first message only. Ongoing threads use platform routing.</span>
                                <span className="text-muted">Activity includes all back-and-forth messages within the hashtag, whether the hashtag was explicitly typed or defaulted.</span>
                            </div>
                        )}
                    </div>
                )}
            </BottomScrollListener>
        </div>
    );
};
export default HashtagOverview;
